import React from 'react';
import { marketRadar } from '../data/index';

interface MarketRadar {
    icon: string;
    eyebrow: string;
    title: string;
    subtitle: string;
    description: string;
    benefits: string[];
    ctaLabel: string;
    ctaHref: string;
}

const radar = marketRadar as MarketRadar;

/**
 * Full-width, eye-catching promotional banner for the Forge Ultra Market Radar™.
 * Deep gradient background with an emerald accent, benefit chips, and a
 * high-contrast CTA that opens the membership page in a new tab.
 */
const MarketRadarBanner: React.FC = () => {
    return (
        <section aria-labelledby="market-radar-heading" className="w-full">
            <div
                className="relative overflow-hidden rounded-3xl
                           bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950
                           border border-emerald-400/30
                           shadow-2xl shadow-emerald-500/10
                           px-6 py-10 sm:px-10 sm:py-12 lg:px-14 lg:py-16"
            >
                {/* Decorative glow accents */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-emerald-500/20 blur-[100px]"
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-indigo-500/20 blur-[100px]"
                />
                {/* Faint radar grid ring */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-10 top-1/2 hidden -translate-y-1/2 lg:block"
                >
                    <div className="h-64 w-64 rounded-full border border-emerald-400/10" />
                    <div className="absolute inset-8 rounded-full border border-emerald-400/10" />
                    <div className="absolute inset-16 rounded-full border border-emerald-400/10" />
                </div>

                <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_auto]">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-3">
                            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-400/15 border border-emerald-400/30 text-emerald-300">
                                <i className={`${radar.icon} text-2xl`} aria-hidden="true"></i>
                            </span>
                            <span className="inline-flex items-center gap-2 px-3 py-1 text-[10px] sm:text-xs font-bold tracking-widest text-emerald-300 uppercase bg-emerald-400/10 rounded-full border border-emerald-400/20">
                                <span className="relative flex h-2 w-2">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                                </span>
                                {radar.eyebrow}
                            </span>
                        </div>

                        <h2
                            id="market-radar-heading"
                            className="mt-5 text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white leading-tight"
                        >
                            {radar.title}
                        </h2>

                        <p className="mt-3 text-base sm:text-lg font-semibold text-emerald-300 leading-snug">
                            {radar.subtitle}
                        </p>

                        <p className="mt-4 text-sm sm:text-[15px] text-slate-300 leading-relaxed">
                            {radar.description}
                        </p>

                        <ul className="mt-6 flex flex-wrap gap-2.5">
                            {radar.benefits.map((benefit) => (
                                <li
                                    key={benefit}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/5 px-3 py-1.5 text-xs sm:text-sm text-slate-200"
                                >
                                    <i className="bx bx-check text-base text-emerald-400 leading-none" aria-hidden="true"></i>
                                    {benefit}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:pl-6">
                        <a
                            href={radar.ctaHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap
                                       rounded-xl px-7 py-4
                                       bg-gradient-to-r from-green-500 to-emerald-400
                                       text-white text-sm sm:text-base font-bold
                                       shadow-lg shadow-emerald-500/30
                                       hover:shadow-xl hover:shadow-emerald-500/50
                                       hover:from-green-400 hover:to-emerald-300
                                       hover:-translate-y-0.5
                                       focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900
                                       transition-all duration-300"
                        >
                            {radar.ctaLabel}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MarketRadarBanner;
